import { useEffect, useState } from 'react';
import api from '@/utils/api';

/**
 * Compact reputation badge data — same shape the server returns from
 * POST /api/public/reputation/badges/batch. Undefined = still loading;
 * null = confirmed no badge (creator has no computed reputation).
 */
export interface CreatorRankBadge {
  position: number;
  total: number;
  percentile: number | null;
}

type BadgeCacheValue = CreatorRankBadge | null; // null = confirmed missing
const cache = new Map<string, BadgeCacheValue>();
const inflight = new Set<string>();
const listeners = new Set<() => void>();

// Debounced batch: usernames requested within the same microtask
// coalesce into one HTTP call. 100 cap mirrors the server-side
// z.max(100) so we chunk if a caller hands us more.
let pending: Set<string> = new Set();
let scheduled = false;

function scheduleFlush() {
  if (scheduled) return;
  scheduled = true;
  // queueMicrotask groups every useCreatorRankBadge mounted in the
  // same render pass into one batch — a feed of 20 posts fires
  // 1 request, not 20. Falls back to setTimeout in the (unlikely)
  // absence of queueMicrotask.
  const run = () => {
    scheduled = false;
    const batch = Array.from(pending);
    pending = new Set();
    if (batch.length === 0) return;
    void flushBatch(batch);
  };
  if (typeof queueMicrotask === 'function') queueMicrotask(run);
  else setTimeout(run, 0);
}

async function flushBatch(usernames: string[]) {
  // Chunk to the server's 100 cap. Feed pages don't get near that
  // but the guard costs nothing.
  const CHUNK = 100;
  for (let i = 0; i < usernames.length; i += CHUNK) {
    const chunk = usernames.slice(i, i + CHUNK);
    try {
      const res = await api.post<{ badges: Record<string, CreatorRankBadge> }>(
        '/public/reputation/badges/batch',
        { usernames: chunk },
      );
      const returned = res.data.badges ?? {};
      for (const u of chunk) {
        cache.set(u, returned[u] ?? null);
        inflight.delete(u);
      }
    } catch {
      // Failed lookup → treat as "no badge" for the session rather
      // than retry-storm on every render. The badge is decorative;
      // its absence is a graceful degrade.
      for (const u of chunk) {
        cache.set(u, null);
        inflight.delete(u);
      }
    }
  }
  for (const notify of listeners) notify();
}

/**
 * Reads (and fetches, on first ask) the reputation badge for a single
 * creator. Batches with other simultaneous asks in the same tick so
 * a feed of N posts costs one HTTP round-trip, not N.
 *
 * Return:
 *   undefined — cache miss, request in flight
 *   null      — creator has no computed reputation (render nothing)
 *   object    — the badge payload
 *
 * Pass `null`/empty string to short-circuit — useful when the parent
 * hasn't hydrated the username yet.
 */
export function useCreatorRankBadge(username: string | null | undefined): BadgeCacheValue | undefined {
  const key = (username ?? '').trim();
  const [, force] = useState(0);

  useEffect(() => {
    if (!key) return;
    // Subscribe first so we don't miss the batch flush if it's
    // already about to fire.
    const notify = () => force((n) => n + 1);
    listeners.add(notify);

    if (!cache.has(key) && !inflight.has(key)) {
      inflight.add(key);
      pending.add(key);
      scheduleFlush();
    }

    return () => { listeners.delete(notify); };
  }, [key]);

  if (!key) return null;
  return cache.get(key);
}

// Exported for tests only.
export const __resetCreatorRankBadgeCache = () => {
  cache.clear();
  inflight.clear();
  pending.clear();
  scheduled = false;
};
